import {
    FetchNewsListPayload,
    FetchNewsListResponse,
    NewsListCard,
    FetchNewsCardSettingsResponse,
} from './newsList.types';
import formatDateString from '../../../helpers/formatDate';
import copyToClipboard from '../../../helpers/copyToClipboard';
import { updatePaginationButtons } from '../pagination/pagination';
import axios from 'axios';

const newsList: HTMLDivElement = document.querySelector('.news-list');

if (newsList) {
    const Grid = newsList.querySelector('.news-list__grid') as HTMLDivElement;
    const Filters = newsList.querySelector('.news-list__search') as HTMLDivElement;
    const ListSearchInput = newsList.querySelector('.news-list__search-input') as HTMLInputElement;
    const DateFrom = newsList.querySelector('.date-range__input-from') as HTMLInputElement;
    const DateTo = newsList.querySelector('.date-range__input-to') as HTMLInputElement;
    const nextPageButton = newsList.querySelector('.pagination__page-next') as HTMLButtonElement;
    const prevPageButton = newsList.querySelector('.pagination__page-prev') as HTMLButtonElement;
    const paginationDropdown = newsList.querySelector('.pagination__dropdown') as HTMLSelectElement;
    const paginationLast = newsList.querySelector('.pagination__page-last') as HTMLSelectElement;
    const paginationFirst = newsList.querySelector('.pagination__page-first') as HTMLSelectElement;
    const pagination = newsList.querySelector('.pagination') as HTMLDivElement;
    const radios: NodeListOf<HTMLInputElement> = newsList.querySelectorAll('.sort-filter__input');
    const currentCulture: string = document.documentElement.getAttribute('culture');
    const dropdownFilters: NodeListOf<HTMLDivElement> = newsList.querySelectorAll('.dropdown-filter');

    let newsCards: NewsListCard[] = [];
    let keyword: string = '';
    let dateFrom: string = '';
    let dateTo: string = '';
    let userTypes: string[] = [];
    let sortBy: number = 1;
    let pageNumber: number = 1;
    let pageSize: number = +paginationDropdown.value;
    let totalPages: number = 0;
    let totalCount: number = 0;
    let hasNextPage: boolean = false;
    let hasPreviousPage: boolean = false;
    let buttonsToShow: number = 3;
    let isLoading: boolean = false;
    let clickedNumber = null;
    let keyWordChanged = false;
    let dateFromChanged = false;
    let dateToChanged = false;

    const restoreDropdownSelections = (dropdown: HTMLDivElement, selectedValues: string[]) => {
        const select = dropdown?.querySelector('.multi-select') as HTMLSelectElement;
        const buttonContainer = dropdown?.querySelector('.dropdown-filter__button-container') as HTMLDivElement;

        if (!select) return;

        selectedValues.forEach((value) => {
            const option = Array.from(select.options).find((item) => item.value === value);

            if (!option) return;

            option.selected = true;
            option.setAttribute('selected', '');

            if (!buttonContainer) return;

            const alreadyRendered = Array.from(buttonContainer.querySelectorAll('.selected-label')).some(
                (label) => label.textContent === value,
            );

            if (alreadyRendered) return;

            const token = document.createElement('div');
            token.classList.add('dropdown-filter__selected-wrapper');

            const label = document.createElement('span');
            label.classList.add('selected-label');
            label.textContent = value;

            const close = document.createElement('a');
            close.classList.add('selected-close');
            close.setAttribute('tabindex', '-1');
            close.setAttribute('data-option', value);
            close.setAttribute('data-hits', '0');
            close.addEventListener('click', (event) => {
                option.selected = false;
                option.removeAttribute('selected');
                token.remove();
                event.stopPropagation();
            });

            token.appendChild(label);
            token.appendChild(close);
            buttonContainer.appendChild(token);
        });
    };

    const initialUrl = new URL(window.location.href);
    restoreDropdownSelections(dropdownFilters[0], initialUrl.searchParams.getAll('userTypes'));

    let newsCardSettings: FetchNewsCardSettingsResponse = {
        Text: '',
    };

    // Functions
    const fetchNewsCardSettings = async () => {
        try {
            const response = await axios.get<FetchNewsCardSettingsResponse>(
                `/api/news/cardsettings?currentCulture=${currentCulture}`
            );

            newsCardSettings = response.data;
        } catch (error) {
            console.error(error);
        }
    };

    const fetchNews = async (page = null) => {
        clickedNumber = page;
        pagination.classList.add('hidden');

        if (clickedNumber) {
            pageNumber = page;
        }

        // Server-side pagination URL handling
        const url = new URL(window.location.href);

        pageNumber = keyWordChanged ? 1 : Number(url.searchParams.get('pageNumber') || 1);
        pageSize = Number(url.searchParams.get('pageSize') || pageSize);
        keyword = keyWordChanged ? keyword : (url.searchParams.get('keyword') || '');
        dateFrom = dateFromChanged ? dateFrom : (url.searchParams.get('dateFrom') || '');
        dateTo = dateToChanged ? dateTo : (url.searchParams.get('dateTo') || '');
        userTypes = userTypes.length == 0 ? (url.searchParams.getAll('userTypes') || []) : userTypes;
        sortBy = Number(url.searchParams.get('sortBy') || 1);

        paginationDropdown.value = pageSize.toString();
        DateFrom.value = dateFrom;
        DateTo.value = dateTo;
        if (dateFrom != '') {
            DateFrom.dispatchEvent(new Event('input'));
        }
        if (dateTo != '') {
            DateTo.dispatchEvent(new Event('input'));
        }

        ListSearchInput.value = keyword;
        radios.forEach(radio => {
            radio.checked = sortBy === +radio.value;
        });

        const payload: FetchNewsListPayload = {
            keyword,
            dateFrom,
            dateTo,
            userTypes,
            sortBy,
            pageNumber,
            pageSize,
            currentCulture,
        };

        isLoading = true;

        disableInputs();

        try {
            let response;
            if (
                (window.location.hostname === 'localhost' && window.location.port === '2000') ||
                window.location.hostname === 'mawhiba-fractal.netlify.app'
            ) {
                response = await axios.post<FetchNewsListResponse>(
                    'https://app-prd-umbracoportal-uaenorth-mawhiba-development.azurewebsites.net/api/news',
                    payload
                );
            } else {
                response = await axios.post<FetchNewsListResponse>('/api/news', payload);
            }
            newsCards = response.data.items;
            if (newsCards.length > 0) {
                pagination.classList.remove('hidden');
            }
            totalPages = response.data.totalPages;
            totalCount = response.data.totalCount;
            hasNextPage = response.data.hasNextPage;
            hasPreviousPage = response.data.hasPreviousPage;
        } catch (error) {
            console.error(error);
            enableInputs();
        } finally {
            updatePaginationButtons(
                totalPages,
                hasPreviousPage,
                hasNextPage,
                pageNumber,
                pageSize,
                totalCount,
                buttonsToShow,
                newsCards,
                renderPage,
                true
            );
            isLoading = false;
            updateCards();
            enableInputs();
        }
    };

    const updateCards = () => {
        Grid.innerHTML = '';

        newsCards.forEach((card: NewsListCard) => {
            const cardElement = document.createElement('a');

            cardElement.setAttribute('href', card.link);
            cardElement.className = 'news__card flex flex--column';

            cardElement.innerHTML = `
            ${card.image !== null ? `<img src='${card.image}' alt='${card.title}' class='news__card-image' />` : ''}
            <p class='news__card-title'>${card.title}</p>
            <p class='news__card-date flex flex--align-center'>
                <img src='/assets/img/img/calendar-icon-yellow.svg' alt='calendar icon' class='news__card-icon' />
                <span class='news__card-text'>
                    ${formatDateString(card.date)}
                </span>
            </p>
            `;

            Grid.appendChild(cardElement);
        });
    };

    const disableInputs = () => {
        pagination.classList.add('disabled');
        Filters.classList.add('disabled');
    };

    const enableInputs = () => {
        pagination.classList.remove('disabled');
        Filters.classList.remove('disabled');
    };

    // Listeners
    paginationDropdown.addEventListener('change', () => {
        pageSize = +paginationDropdown.value;
        pageNumber = 1;
        renderPage();
    });

    nextPageButton.addEventListener('click', () => {
        pageNumber++;

        renderPage();
    });

    prevPageButton.addEventListener('click', () => {
        pageNumber--;

        renderPage();
    });

    paginationFirst.addEventListener('click', () => {
        pageNumber = 1;
        renderPage();
    });

    paginationLast.addEventListener('click', () => {
        pageNumber = totalPages;
        renderPage();
    });

    let timeout = null;
    ListSearchInput.addEventListener('input', (e) => {
        clearTimeout(timeout);

        timeout = setTimeout(function () {
            keyword = ListSearchInput.value.trim();
            keyWordChanged = true;
            pageNumber = 1;
            fetchNews();
        }, 1000);
    });

    DateFrom.addEventListener('change', () => {
        dateFrom = DateFrom.value;
        dateFromChanged = true;
        pageNumber = 1;
        fetchNews();
    });

    DateTo.addEventListener('change', () => {
        dateTo = DateTo.value;
        dateToChanged = true;
        pageNumber = 1;
        fetchNews();
    });

    radios.forEach((radio) => {
        // 1 = Latest
        // 2 = A-Z
        // 3 = Z-A

        radio.addEventListener('change', () => {
            const checkedRadio = newsList.querySelector('input[name="sort-filter"]:checked') as HTMLInputElement;
            const checkedRadioValue = checkedRadio.value;
            sortBy = +checkedRadioValue;
            renderPage();
        });
    });

    // Observe changes in dropdown filters
    dropdownFilters.forEach((dropdown, index) => {
        const optionsContainer = dropdown.querySelector('.dropdown-filter__button-container') as HTMLDivElement | null;

        if (optionsContainer) {
            const config: MutationObserverInit = { childList: true };

            const callback: MutationCallback = (mutationsList: MutationRecord[], observer: MutationObserver) => {
                for (const mutation of mutationsList) {
                    if (mutation.type === 'childList') {
                        switch (index) {
                            case 0:
                                const userTypesOptions = [];
                                Array.from(optionsContainer.children).forEach((element) => {
                                    userTypesOptions.push(element.textContent);
                                });
                                userTypes = userTypesOptions;
                                break;
                            default:
                                break;
                        }

                        pageNumber = 1;
                        renderPage();
                    }
                }
            };

            const observer: MutationObserver = new MutationObserver(callback);

            observer.observe(optionsContainer, config);
        }
    });
    
    const initializeEventsList = async () => {
        await fetchNewsCardSettings();
        await fetchNews();
    };

    // Call the initialize function
    initializeEventsList();

    const shareButtons: NodeListOf<HTMLButtonElement> = document.querySelectorAll(".shareButton");
    if (shareButtons && shareButtons.length > 0) {
        shareButtons.forEach(button => {
        button.addEventListener('click', (e) => {
            copyToClipboard(button);                 
        });   
        })
    }

    const renderPage = (page = null) => 
    {
        if (page) {
            pageNumber = page;
        }
        const url = new URL(window.location.href.replace(window.location.search, ''));
        url.searchParams.set('pageNumber', pageNumber.toString());
        url.searchParams.set('pageSize', pageSize.toString());
        if(keyword) { url.searchParams.set('keyword', keyword);}
        if(dateFrom) { url.searchParams.set('dateFrom', dateFrom); }
        if(dateTo) { url.searchParams.set('dateTo', dateTo); }
        userTypes.forEach(( userType ) => {
            url.searchParams.append('userTypes', userType);
        });
        if(sortBy) { url.searchParams.set('sortBy', sortBy.toString()); }
        if (page == null)
        {
            window.location.href = url.toString();
        }
        return url.toString();
    }
}
